import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ConsigneeService } from '../../../services/consignee/consignee.service';
import { GlobalStateService } from '../../../services/global/global-state.service';
import { Consignee, ConsigneeFormData } from '../../../shared/models/consignee.interface';
import { PaginatedResponse, PaginationInfo } from '../../../shared/models/api-response.interface';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-consignee-list',
  templateUrl: './consignee-list.component.html',
  styleUrls: ['./consignee-list.component.scss']
})
export class ConsigneeListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() companyId: string = '';
  @Input() companyName: string = '';
  @Output() consigneeSelected = new EventEmitter<Consignee>();

  // Component properties
  consignees: Consignee[] = [];
  selectedConsigneeId: string = '';
  searchTerm: string = '';
  isLoading: boolean = false;
  isAddingConsignee: boolean = false;
  countryList: any[] = [];

  // Pagination
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Form Group for adding new consignee
  consigneeForm: FormGroup;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private consigneeService: ConsigneeService,
    private globalStateService: GlobalStateService,
    private fb: FormBuilder
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.updateCompanyId();
    this.loadCountries();
    this.loadConsignees();
  }

  // Initialize the reactive form
  private initializeForm(): void {
    this.consigneeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      address: ['', [Validators.required, Validators.minLength(5)]],
      city: ['', [Validators.required, Validators.minLength(2)]],
      country: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telephone: ['', [Validators.required, Validators.minLength(10)]],
      company_id: ['']
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && !changes['companyId'].firstChange) {
      this.updateCompanyId();
      this.loadConsignees();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Load countries from global service
  private loadCountries(): void {
    const subscription = this.globalStateService.GetCountries().subscribe(countries => {
      this.countryList = countries || [];
    });
    this.subscriptions.push(subscription);
  }

  // Update company_id in form when companyId input changes
  private updateCompanyId(): void {
    if (this.companyId && this.consigneeForm) {
      this.consigneeForm.patchValue({ company_id: this.companyId });
    }
  }

  // Load consignees from API
  loadConsignees(page: number = 1): void {
    this.isLoading = true;

    const filters = this.companyId ? { company_id: this.companyId } : {};

    const subscription = this.consigneeService.GetData(
      page.toString(),
      this.searchTerm,
      filters
    ).subscribe({
      next: (response: PaginatedResponse<Consignee>) => {
        if (response.status && response.data) {
          this.consignees = response.data.data;
          this.paginationInfo = response.data.pagination;
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading consignees:', error);
        this.isLoading = false;
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load consignees. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });

    this.subscriptions.push(subscription);
  }

  // Add new consignee
  addConsignee(): void {
    // Ensure company_id is set
    if (!this.companyId) {
      Swal.fire({
        title: 'Error!',
        text: 'No company selected. Please select a company first.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Mark all fields as touched to show validation errors
    this.consigneeForm.markAllAsTouched();

    // Validate form
    if (this.consigneeForm.invalid) {
      return;
    }

    // Ensure company_id is set in the form data
    this.consigneeForm.patchValue({ company_id: this.companyId });

    this.isAddingConsignee = true;

    const formData = this.consigneeForm.value as ConsigneeFormData;
    const subscription = this.consigneeService.AddData(formData).subscribe({
      next: (response) => {
        if (response.status) {
          Swal.fire({
            title: 'Success!',
            text: 'Consignee added successfully.',
            icon: 'success',
            confirmButtonText: 'OK'
          });

          // Reset form
          this.resetForm();

          // Reload consignees
          this.loadConsignees(this.paginationInfo.currentPage);
        } else {
          Swal.fire({
            title: 'Error!',
            text: response.message || 'Failed to add consignee.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
        this.isAddingConsignee = false;
      },
      error: (error) => {
        console.error('Error adding consignee:', error);
        this.isAddingConsignee = false;
        Swal.fire({
          title: 'Error!',
          text: 'Failed to add consignee. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    });

    this.subscriptions.push(subscription);
  }

  // Reset form
  resetForm(): void {
    this.consigneeForm.reset();
    this.consigneeForm.patchValue({ company_id: this.companyId });
  }

  // Search consignees
  searchConsignees(): void {
    this.loadConsignees(1); // Reset to first page when searching
  }

  // Clear search
  clearSearch(): void {
    this.searchTerm = '';
    this.loadConsignees(1);
  }

  // Toggle individual consignee selection (radio button behavior)
  selectConsignee(consigneeId: string): void {
    this.selectedConsigneeId = consigneeId;
  }

  // Check if consignee is selected
  isConsigneeSelected(consigneeId: string): boolean {
    return this.selectedConsigneeId === consigneeId;
  }

  // Choose selected consignee and emit to parent
  chooseConsignee(consigneeId: string): void {
    const selectedConsignee = this.consignees.find(c => c._id === consigneeId);
    if (selectedConsignee) {
      this.consigneeSelected.emit(selectedConsignee);
      this.closeModal();
    }
  }

  // Handle pagination
  onPageChange(page: number): void {
    this.loadConsignees(page);
  }

  // Close modal
  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_consignee_list');
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  // Get total records count
  getTotalRecords(): number {
    return this.paginationInfo.total;
  }

  // Form validation helper methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.consigneeForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.consigneeForm.get(fieldName);
    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        const requiredLength = field.errors['minlength'].requiredLength;
        return `${this.getFieldDisplayName(fieldName)} must be at least ${requiredLength} characters`;
      }
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      name: 'Name',
      address: 'Address',
      city: 'City',
      country: 'Country',
      email: 'Email',
      telephone: 'Telephone'
    };
    return displayNames[fieldName] || fieldName;
  }
}
